(() => {
  if (customElements.get('babauba-hot-deals')) return;

  const STORAGE_PREFIX = 'babauba:hot-deals:reveal:';
  const VISIT_STORAGE_KEY = 'babauba:hot-deals:last-visit';
  const SEEN_DEAL_STORAGE_KEY = 'babauba:hot-deals:last-seen-key';
  const STEP_DURATIONS = [0, 900, 600, 800];

  class BabaubaHotDeals extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.revealButton = this.querySelector('[data-hot-deal-reveal]');
      this.revealLabel = this.querySelector('[data-reveal-label]');
      this.progress = this.querySelector('[data-hot-deal-progress]');
      this.cards = this.querySelector('[data-hot-deal-cards]');
      this.cardElements = [...this.querySelectorAll('[data-hot-deal-card]')];
      this.pack = this.querySelector('[data-hot-deal-pack]');
      this.tomorrow = this.querySelector('[data-hot-deal-tomorrow]');
      this.announcement = this.querySelector('[data-hot-deal-announcement]');
      this.dialog = this.querySelector('[data-hot-deal-dialog]');
      this.dialogViewport = this.querySelector('[data-hot-deal-dialog-viewport]');
      this.dialogCloseButton = this.querySelector('[data-hot-deal-dialog-close]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dealKey = this.dataset.dealKey || 'unavailable';
      this.newBadgeLabel = this.dataset.newBadgeLabel || 'Neu';
      this.storageKey = `${STORAGE_PREFIX}${encodeURIComponent(this.dealKey)}`;
      this.isAnimating = false;
      this.step = this.restoreStep();
      this.isNewDeal = this.restoreDealNewness();

      this.onRevealClick = this.revealNext.bind(this);
      this.onCardRevealClick = this.handleCardRevealClick.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onProductClick = this.handleProductClick.bind(this);
      this.onFormSubmit = this.handleFormSubmit.bind(this);
      this.onProductAdded = this.handleProductAdded.bind(this);
      this.onProductError = this.handleProductError.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDialogClick = this.handleDialogClick.bind(this);
      this.onDialogCancel = this.handleDialogCancel.bind(this);
      this.onDialogKeydown = this.handleDialogKeydown.bind(this);
      this.onDialogClose = this.handleDialogClose.bind(this);
      this.onLocationChange = this.syncDialogWithLocation.bind(this);

      this.revealButton?.addEventListener('click', this.onRevealClick);
      this.pack?.addEventListener('click', this.onRevealClick);
      this.addEventListener('click', this.onCardRevealClick);
      this.addEventListener('change', this.onVariantChange);
      this.addEventListener('click', this.onProductClick);
      this.addEventListener('submit', this.onFormSubmit);
      document.addEventListener('product-ajax:added', this.onProductAdded);
      document.addEventListener('product-ajax:error', this.onProductError);

      if (this.dialog) {
        document.documentElement.classList.add('babauba-hot-deals-available');
        document.querySelectorAll('[data-hot-deal-external-trigger]').forEach((trigger) => {
          trigger.hidden = false;
        });
        document.addEventListener('click', this.onDocumentClick);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('cancel', this.onDialogCancel);
        this.dialog.addEventListener('keydown', this.onDialogKeydown);
        this.dialog.addEventListener('close', this.onDialogClose);
        window.addEventListener('popstate', this.onLocationChange);
        window.addEventListener('hashchange', this.onLocationChange);
      }

      this.updateEntryBadges();
      this.applyStep(this.step, false, false);
      this.startCountdowns();
      this.trackNextDayReturn();

      if (this.dialog && window.location.hash === '#hot-deals') {
        window.requestAnimationFrame(() => this.openDialog(null, false));
      }
    }

    disconnectedCallback() {
      this.revealButton?.removeEventListener('click', this.onRevealClick);
      this.pack?.removeEventListener('click', this.onRevealClick);
      this.removeEventListener('click', this.onCardRevealClick);
      this.removeEventListener('change', this.onVariantChange);
      this.removeEventListener('click', this.onProductClick);
      this.removeEventListener('submit', this.onFormSubmit);
      document.removeEventListener('product-ajax:added', this.onProductAdded);
      document.removeEventListener('product-ajax:error', this.onProductError);
      document.removeEventListener('click', this.onDocumentClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      this.dialog?.removeEventListener('keydown', this.onDialogKeydown);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      window.removeEventListener('popstate', this.onLocationChange);
      window.removeEventListener('hashchange', this.onLocationChange);
      this.countdownIntervals?.forEach((interval) => window.clearInterval(interval));
      window.clearTimeout(this.pendingCartAddTimeout);

      if (this.dialog?.open) {
        this.dialog.close();
        document.body.classList.remove('babauba-hot-deals-modal-open');
      }

      window.queueMicrotask(() => {
        if (!document.querySelector('babauba-hot-deals [data-hot-deal-dialog]')) {
          document.documentElement.classList.remove('babauba-hot-deals-available');
          document.body.classList.remove('babauba-hot-deals-modal-open');
          document.querySelectorAll('[data-hot-deal-external-trigger]').forEach((trigger) => {
            trigger.hidden = true;
          });
          this.clearEntryBadges();
        }
      });
    }

    restoreStep() {
      if (!this.revealButton || this.dealKey === 'unavailable') return 0;

      let restoredState = null;
      try {
        const storedValue = window.localStorage.getItem(this.storageKey);
        restoredState = storedValue ? JSON.parse(storedValue) : null;
      } catch (error) {
        restoredState = null;
      }

      const legacyStep = Number.isInteger(restoredState) ? this.clampStep(restoredState) : null;
      const fallbackStep =
        legacyStep ?? this.clampStep(restoredState?.step ?? (restoredState?.opened ? 1 : 0));

      this.cardElements.forEach((card) => {
        const productId = card.dataset.productId;
        const storedCardStep = restoredState?.cards?.[productId];
        card.dataset.cardStep = String(this.clampStep(storedCardStep ?? fallbackStep));
      });

      this.isOpened =
        legacyStep !== null
          ? legacyStep >= 1
          : Boolean(restoredState?.opened || this.cardElements.some((card) => this.getCardStep(card) >= 1));

      if (!this.isOpened) {
        this.cardElements.forEach((card) => {
          card.dataset.cardStep = '0';
        });
      }

      return this.getAggregateStep();
    }

    storeStep() {
      try {
        const cards = {};
        this.cardElements.forEach((card) => {
          if (card.dataset.productId) {
            cards[card.dataset.productId] = this.getCardStep(card);
          }
        });

        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            version: 2,
            opened: this.isOpened,
            cards,
          })
        );
      } catch (error) {
        // The reveal still works when storage is blocked.
      }
    }

    restoreDealNewness() {
      if (this.dealKey === 'unavailable') return false;

      try {
        const lastSeenDealKey = window.localStorage.getItem(SEEN_DEAL_STORAGE_KEY);

        if (this.step > 0) {
          if (lastSeenDealKey !== this.dealKey) {
            window.localStorage.setItem(SEEN_DEAL_STORAGE_KEY, this.dealKey);
          }
          return false;
        }

        return lastSeenDealKey !== this.dealKey;
      } catch (error) {
        return this.step === 0;
      }
    }

    markDealSeen() {
      if (!this.isNewDeal || this.dealKey === 'unavailable') return;

      this.isNewDeal = false;
      try {
        window.localStorage.setItem(SEEN_DEAL_STORAGE_KEY, this.dealKey);
      } catch (error) {
        // The badge still disappears for the current page when storage is blocked.
      }
      this.updateEntryBadges();
    }

    updateEntryBadges() {
      document.querySelectorAll('a[href="#hot-deals"]').forEach((entry) => {
        const announcementSticker = entry.querySelector('.announcement-promotion__sticker');
        entry.toggleAttribute('data-hot-deal-new', this.isNewDeal);

        if (announcementSticker) {
          if (!announcementSticker.dataset.hotDealDefaultLabel) {
            announcementSticker.dataset.hotDealDefaultLabel = announcementSticker.textContent.trim();
          }
          announcementSticker.textContent = this.isNewDeal
            ? this.newBadgeLabel
            : announcementSticker.dataset.hotDealDefaultLabel;
          announcementSticker.classList.toggle('announcement-promotion__sticker--new-deal', this.isNewDeal);
          return;
        }

        let badge = entry.querySelector('[data-hot-deal-new-badge]');
        if (!badge && this.isNewDeal) {
          badge = document.createElement('span');
          badge.className = 'babauba-hot-deals-new-badge';
          badge.dataset.hotDealNewBadge = '';
          entry.prepend(badge);
        }

        if (badge) {
          badge.textContent = this.newBadgeLabel;
          badge.hidden = !this.isNewDeal;
        }
      });
    }

    clearEntryBadges() {
      document.querySelectorAll('a[href="#hot-deals"]').forEach((entry) => {
        entry.removeAttribute('data-hot-deal-new');
        entry.querySelectorAll('[data-hot-deal-new-badge]').forEach((badge) => badge.remove());

        const announcementSticker = entry.querySelector('.announcement-promotion__sticker');
        if (!announcementSticker) return;

        if (announcementSticker.dataset.hotDealDefaultLabel) {
          announcementSticker.textContent = announcementSticker.dataset.hotDealDefaultLabel;
        }
        announcementSticker.classList.remove('announcement-promotion__sticker--new-deal');
      });
    }

    clampStep(step) {
      const parsedStep = Number.parseInt(step, 10);
      return Number.isInteger(parsedStep) ? Math.min(Math.max(parsedStep, 0), 3) : 0;
    }

    getCardStep(card) {
      return this.clampStep(card?.dataset.cardStep);
    }

    getAggregateStep() {
      if (!this.isOpened || !this.cardElements.length) return 0;
      return Math.min(...this.cardElements.map((card) => Math.max(1, this.getCardStep(card))));
    }

    async revealNext() {
      if (this.isAnimating || this.step >= 3) return;

      const nextStep = this.step + 1;
      this.isAnimating = true;
      this.setBusy(true);

      if (nextStep === 1) {
        this.isOpened = true;
        this.track('hot_deal_reveal_started');
      }

      this.cardElements.forEach((card) => {
        if (this.getCardStep(card) < nextStep) {
          card.dataset.cardStep = String(nextStep);
        }
      });
      this.step = this.getAggregateStep();
      this.storeStep();
      this.applyStep(this.step, true);
      this.track(this.eventNameForStep(nextStep));

      const duration = this.reducedMotion.matches ? 140 : STEP_DURATIONS[nextStep];
      await new Promise((resolve) => window.setTimeout(resolve, duration));

      this.classList.remove('is-transitioning');
      this.isAnimating = false;
      this.setBusy(false);

      if (this.step === 3) {
        this.revealButton.hidden = true;
        this.tomorrow.hidden = false;
        this.querySelector('[data-hot-deal-product-link], .babauba-hot-deal-card__cta')?.focus({ preventScroll: true });
      }
    }

    async revealCard(card) {
      const currentCardStep = this.getCardStep(card);
      if (this.isAnimating || !this.isOpened || currentCardStep < 1 || currentCardStep >= 3) return;

      const nextCardStep = currentCardStep + 1;
      const previousAggregateStep = this.step;
      const productTitle = card.dataset.productTitle || 'Hot Deal';

      this.isAnimating = true;
      this.setBusy(true);
      card.dataset.cardStep = String(nextCardStep);
      card.classList.add('is-card-transitioning');

      this.step = this.getAggregateStep();
      const completesAllCards = this.step === 3 && previousAggregateStep < 3;
      this.classList.toggle('is-individual-completing', completesAllCards);
      this.storeStep();
      this.applyStep(this.step, false, false);
      this.track(this.eventNameForStep(nextCardStep), {
        ...this.productDetails(card),
        card_reveal_step: nextCardStep,
        reveal_interaction: 'card',
      });

      if (nextCardStep === 2) {
        this.announce(`${productTitle}: Das Design ist aufgedeckt.`);
      } else {
        this.announce(`${productTitle}: Das Produkt ist aufgedeckt.`);
      }

      const duration = this.reducedMotion.matches ? 140 : STEP_DURATIONS[nextCardStep];
      await new Promise((resolve) => window.setTimeout(resolve, duration));

      card.classList.remove('is-card-transitioning');
      this.classList.remove('is-individual-completing');
      this.isAnimating = false;
      this.setBusy(false);

      if (nextCardStep === 3) {
        card
          .querySelector('[data-hot-deal-product-link], .babauba-hot-deal-card__cta')
          ?.focus({ preventScroll: true });
      }
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);
      if (this.revealButton) {
        this.revealButton.disabled = isBusy;
      }
      if (this.pack) {
        this.pack.disabled = isBusy || this.step >= 1;
      }
      this.querySelectorAll('[data-hot-deal-mystery]').forEach((mystery) => {
        const card = mystery.closest('[data-hot-deal-card]');
        mystery.disabled = isBusy || this.getCardStep(card) >= 3;
      });
    }

    applyStep(step, animate, shouldAnnounce = true) {
      this.dataset.step = String(step);
      this.classList.toggle('is-transitioning', animate);

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(step));
      }
      this.updateTriggerLabels(step);

      this.querySelectorAll('[data-progress-step]').forEach((item) => {
        const itemStep = Number.parseInt(item.dataset.progressStep, 10);
        item.classList.toggle('is-complete', itemStep <= step);
      });

      this.updateAccessibility(step);

      if (!this.revealButton) return;

      if (step === 0) {
        this.revealButton.hidden = false;
        this.tomorrow.hidden = true;
        this.revealLabel.textContent = this.dataset.openLabel || 'Jetzt öffnen';
      } else if (step === 1) {
        this.revealButton.hidden = false;
        this.tomorrow.hidden = true;
        this.revealLabel.textContent = this.dataset.patternLabel || 'Muster aufdecken';
        if (shouldAnnounce) this.announce('Schritt 1 von 3: Die Rabatte sind aufgedeckt.');
      } else if (step === 2) {
        this.revealButton.hidden = false;
        this.tomorrow.hidden = true;
        this.revealLabel.textContent = this.dataset.productLabel || 'Produkte enthüllen';
        if (shouldAnnounce) this.announce('Schritt 2 von 3: Die Designs sind aufgedeckt.');
      } else {
        if (!animate) this.revealButton.hidden = true;
        this.tomorrow.hidden = false;
        if (shouldAnnounce) this.announce('Schritt 3 von 3: Die Hot-Deal-Produkte sind aufgedeckt.');
      }
    }

    updateTriggerLabels(step) {
      const label =
        step === 3
          ? this.dataset.triggerRevealedLabel || 'Hot Deals ansehen'
          : this.dataset.triggerLabel || 'Geheime Hot Deals öffnen';

      this.querySelectorAll('[data-hot-deal-trigger-label]').forEach((triggerLabel) => {
        triggerLabel.textContent = label;
      });
      this.classList.toggle('is-revealed', step === 3);
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-hot-deal-dialog-open], a[href="#hot-deals"]');
      if (!trigger || !this.dialog) return;

      const controlledDialogId = trigger.getAttribute('aria-controls');
      if (controlledDialogId && controlledDialogId !== this.dialog.id) return;

      if (!controlledDialogId) {
        const firstDialog = document.querySelector('babauba-hot-deals [data-hot-deal-dialog]');
        if (firstDialog !== this.dialog) return;
      }

      event.preventDefault();
      this.openDialog(trigger, true);
    }

    handleDialogClick(event) {
      if (event.target.closest('[data-hot-deal-dialog-close]')) {
        event.preventDefault();
        this.requestDialogClose();
        return;
      }

      if (event.target === this.dialog) {
        this.requestDialogClose();
      }
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.requestDialogClose();
    }

    handleDialogKeydown(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      this.requestDialogClose();
    }

    openDialog(trigger, updateHistory = true) {
      if (!this.dialog || this.dialog.open) return;

      this.returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('role', 'dialog');
        this.dialog.setAttribute('aria-modal', 'true');
      }

      document.body.classList.add('babauba-hot-deals-modal-open');

      if (updateHistory && window.location.hash !== '#hot-deals') {
        const nextState = {
          ...(window.history.state || {}),
          babaubaHotDeals: this.dataset.sectionId || true,
        };
        window.history.pushState(nextState, '', `${window.location.pathname}${window.location.search}#hot-deals`);
      }

      window.requestAnimationFrame(() => {
        this.dialogCloseButton?.focus({ preventScroll: true });
      });

      this.markDealSeen();
      this.markAreaSeen();
      this.track('hot_deal_modal_opened');
    }

    requestDialogClose() {
      if (!this.dialog?.open) return;

      this.closeDialog(true);

      if (window.location.hash !== '#hot-deals') return;

      if (window.history.state?.babaubaHotDeals) {
        window.history.back();
      } else {
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}`
        );
      }
    }

    closeDialog(restoreFocus = true) {
      if (!this.dialog?.open) return;

      this.shouldRestoreFocus = restoreFocus;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    handleDialogClose() {
      document.body.classList.remove('babauba-hot-deals-modal-open');
      this.track('hot_deal_modal_closed');

      if (this.shouldRestoreFocus !== false && this.returnFocus?.isConnected) {
        this.returnFocus.focus({ preventScroll: true });
      }

      this.shouldRestoreFocus = true;
    }

    syncDialogWithLocation() {
      if (window.location.hash === '#hot-deals') {
        this.openDialog(null, false);
      } else {
        this.closeDialog(true);
      }
    }

    updateAccessibility(step) {
      if (this.pack) {
        const packIsVisible = step === 0;
        this.pack.setAttribute('aria-hidden', packIsVisible ? 'false' : 'true');
        this.pack.toggleAttribute('inert', !packIsVisible);
        this.pack.disabled = !packIsVisible || this.isAnimating;
      }

      if (!this.cards) return;

      const cardsAreVisible = step >= 1;
      this.cards.setAttribute('aria-hidden', cardsAreVisible ? 'false' : 'true');
      this.cards.toggleAttribute('inert', !cardsAreVisible);

      this.cardElements.forEach((card) => {
        const cardStep = this.getCardStep(card);
        const mystery = card.querySelector('[data-hot-deal-mystery]');
        const product = card.querySelector('[data-hot-deal-product]');
        const isMysteryVisible = cardsAreVisible && cardStep >= 1 && cardStep < 3;
        const isProductVisible = cardsAreVisible && cardStep === 3;

        if (!mystery || !product) return;

        const nextAction =
          cardStep <= 1
            ? this.dataset.patternLabel || 'Muster aufdecken'
            : this.dataset.productLabel || 'Produkt enthüllen';
        const productTitle = card.dataset.productTitle || 'Hot Deal';

        mystery.setAttribute('aria-hidden', isMysteryVisible ? 'false' : 'true');
        mystery.toggleAttribute('inert', !isMysteryVisible);
        mystery.disabled = !isMysteryVisible || this.isAnimating;
        mystery.setAttribute('aria-label', `${productTitle}: ${nextAction}`);

        product.setAttribute('aria-hidden', isProductVisible ? 'false' : 'true');
        product.toggleAttribute('inert', !isProductVisible);
      });
    }

    handleCardRevealClick(event) {
      const mystery = event.target.closest('[data-hot-deal-mystery]');
      if (!mystery) return;

      const card = mystery.closest('[data-hot-deal-card]');
      if (!card) return;

      event.preventDefault();
      this.revealCard(card);
    }

    announce(message) {
      if (!this.announcement) return;
      this.announcement.textContent = '';
      window.requestAnimationFrame(() => {
        this.announcement.textContent = message;
      });
    }

    eventNameForStep(step) {
      return ['hot_deal_area_viewed', 'hot_deal_discount_revealed', 'hot_deal_pattern_revealed', 'hot_deal_product_revealed'][step];
    }

    handleVariantChange(event) {
      const variantInput = event.target.closest('[data-hot-deal-variant]');
      if (!variantInput || !variantInput.checked) return;

      const card = variantInput.closest('[data-hot-deal-card]');
      if (!card) return;

      const variantIdInput = card.querySelector('[data-hot-deal-variant-id]');
      const price = card.querySelector('[data-hot-deal-price]');
      const compare = card.querySelector('[data-hot-deal-compare]');
      const discount = card.querySelector('[data-hot-deal-discount]');
      const productDiscount = card.querySelector('[data-hot-deal-product-discount]');

      if (variantIdInput) variantIdInput.value = variantInput.value;
      if (price) price.textContent = variantInput.dataset.price;
      if (compare) compare.textContent = variantInput.dataset.compare;
      if (discount) discount.textContent = variantInput.dataset.discount;
      if (productDiscount) productDiscount.textContent = variantInput.dataset.discount;
    }

    handleProductClick(event) {
      const link = event.target.closest('[data-hot-deal-product-link]');
      if (!link) return;

      const card = link.closest('[data-hot-deal-card]');
      if (this.getCardStep(card) !== 3) return;

      this.track('hot_deal_product_clicked', this.productDetails(card));
    }

    handleFormSubmit(event) {
      const form = event.target.closest('.babauba-hot-deal-card__form');
      if (!form) return;

      const card = form.closest('[data-hot-deal-card]');
      if (this.getCardStep(card) !== 3) return;

      const variantId = form.querySelector('[data-hot-deal-variant-id]')?.value;
      const cartDetails = {
        ...this.productDetails(card),
        variant_id: variantId || null,
      };
      const expectsAjaxResponse =
        !document.body.classList.contains('cart-template') && window.FoxTheme?.settings?.cartType !== 'page';

      if (!expectsAjaxResponse) {
        this.track('hot_deal_cart_added', cartDetails);
        return;
      }

      this.pendingCartAdd = cartDetails;
      window.clearTimeout(this.pendingCartAddTimeout);
      this.pendingCartAddTimeout = window.setTimeout(() => {
        this.pendingCartAdd = null;
      }, 15000);
    }

    handleProductAdded() {
      if (!this.pendingCartAdd) return;

      this.track('hot_deal_cart_added', this.pendingCartAdd);
      this.pendingCartAdd = null;
      window.clearTimeout(this.pendingCartAddTimeout);
    }

    handleProductError() {
      this.pendingCartAdd = null;
      window.clearTimeout(this.pendingCartAddTimeout);
    }

    productDetails(card) {
      if (!card) return {};
      return {
        product_id: card.dataset.productId || null,
        product_title: card.dataset.productTitle || null,
        position: Number.parseInt(card.dataset.position, 10) || null,
      };
    }

    markAreaSeen() {
      if (this.dealKey === 'unavailable') return;

      const seenKey = `babauba:hot-deals:area-seen:${this.dealKey}`;
      try {
        if (window.sessionStorage.getItem(seenKey)) return;
        window.sessionStorage.setItem(seenKey, 'true');
      } catch (error) {
        // Tracking remains available without sessionStorage.
      }
      this.track('hot_deal_area_viewed');
    }

    trackNextDayReturn() {
      if (this.dealKey === 'unavailable') return;

      const now = new Date();
      const dayNumber = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000;

      try {
        const previous = JSON.parse(window.localStorage.getItem(VISIT_STORAGE_KEY) || 'null');
        if (previous && dayNumber - previous.dayNumber === 1 && previous.returnTrackedFor !== dayNumber) {
          this.track('hot_deal_next_day_return', {
            previous_deal_key: previous.dealKey || null,
          });
        }

        window.localStorage.setItem(
          VISIT_STORAGE_KEY,
          JSON.stringify({
            dayNumber,
            dealKey: this.dealKey,
            returnTrackedFor: dayNumber - previous?.dayNumber === 1 ? dayNumber : previous?.returnTrackedFor,
          })
        );
      } catch (error) {
        // Returning visitors cannot be identified when storage is blocked.
      }
    }

    startCountdowns() {
      this.countdownIntervals = [];
      this.querySelectorAll('[data-hot-deal-countdown]').forEach((countdown) => {
        const target = Number.parseInt(countdown.dataset.countdownTarget, 10) * 1000;
        const output = countdown.querySelector('[data-countdown-value]');
        if (!target || !output) return;

        let reloadQueued = false;
        const update = () => {
          const remainingSeconds = Math.max(0, Math.floor((target - Date.now()) / 1000));
          output.textContent = this.formatDuration(remainingSeconds);
          countdown.setAttribute('aria-label', `${countdown.textContent.trim()}`);

          if (remainingSeconds === 0 && !reloadQueued) {
            reloadQueued = true;
            window.setTimeout(() => window.location.reload(), 1000);
          }
        };

        update();
        this.countdownIntervals.push(window.setInterval(update, 1000));
      });
    }

    formatDuration(totalSeconds) {
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const number = new Intl.NumberFormat(document.documentElement.lang || 'de', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
      const clock = [hours, minutes, seconds].map((value) => number.format(value)).join(':');
      return days > 0 ? `${days} T · ${clock}` : clock;
    }

    track(eventName, details = {}) {
      if (!eventName) return;

      const privacyApi = window.Shopify?.customerPrivacy;
      if (privacyApi?.analyticsProcessingAllowed && !privacyApi.analyticsProcessingAllowed()) return;

      const payload = {
        event: eventName,
        hot_deal_key: this.dealKey,
        section_id: this.dataset.sectionId || null,
        reveal_step: this.step,
        product_count: Number.parseInt(this.dataset.productCount, 10) || null,
        ...details,
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      document.dispatchEvent(
        new CustomEvent('babauba:hot-deal', {
          detail: payload,
        })
      );

      try {
        const publishResult = window.Shopify?.analytics?.publish?.(`babauba:${eventName}`, payload);
        publishResult?.catch?.(() => {});
      } catch (error) {
        // dataLayer and the DOM event remain the stable tracking interfaces.
      }
    }
  }

  customElements.define('babauba-hot-deals', BabaubaHotDeals);
})();
